import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { OpenApiRequestStatus, useOpenApiRequest } from "typescript-request-utils";

import { ApplicationStatus, applicationStatusToString } from "../../api/enums/ApplicationStatus";
import { Application } from "../../api/models/Application";
import ApplicationModal from "../../framework/components/modal/ApplicationModal";
import { AcceptIcon, DeclineIcon } from "../../framework/components/modal/modalIcons";
import { TableColumn, TableRow } from "../../framework/components/table/tableComponents";
import { getApiRegistry } from "../../framework/state/apiRegistry";
import { ViewType } from "../../page/enums/ViewType";
import useCustomTranslation from "../hooks/CustomUseTranslation";

const Status = styled.p`
    color: ${props => props.theme.status === ApplicationStatus.ACCEPTED ? "#7FB722" : props.theme.status === ApplicationStatus.DECLINED ? "#D50000" : "#686868"};
`
const ActionButton = styled.button`
    margin-left: 10px;
    border: 1.5px solid;
    border-color: ${props => props.theme.actionType === 'Accept' ? "#7FB722" : "#D50000"};
    border-radius: 5px;
    background-color: white;
    height: 30px;
    width: 30px;
    padding: 0;
    float: right;
    cursor: pointer;
`

export interface ApplicationItemProps {
    application: Application;
    type: ViewType;
    onUpdate: () => void;
}

const ApplicationItem: FC<ApplicationItemProps> = (props) => {
    const { t } = useCustomTranslation();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalType, setModalType] = useState<string>('');
    const [modalUpdateError, setModalUpdateError] = useState<boolean>(false);
    const [applicationRequestPending, setApplicationRequestPending] = useState<boolean>(false);

    const [updateApplicationRequest, updateApplication] = useOpenApiRequest(getApiRegistry().applicationsApi.update);
    const [deleteApplicationRequest, deleteApplication] = useOpenApiRequest(getApiRegistry().applicationsApi.delete);

    var createdDate = props.application.created ? props.application.created.toLocaleDateString("fi-FI") : '';
    var startDate = props.application.startDate ? props.application.startDate.toLocaleDateString("fi-FI") : '';
    var endDate = props.application.endDate ? props.application.endDate.toLocaleDateString("fi-FI") : '';

    useEffect(() => {
        if (updateApplicationRequest.type === OpenApiRequestStatus.Pending || deleteApplicationRequest.type === OpenApiRequestStatus.Pending) {
            setApplicationRequestPending(true);
        }
        if (updateApplicationRequest.type === OpenApiRequestStatus.Complete || deleteApplicationRequest.type === OpenApiRequestStatus.Complete) {
            setShowModal(false);
            setModalUpdateError(false);
            setApplicationRequestPending(false);
            props.onUpdate();
        } else if (updateApplicationRequest.type === OpenApiRequestStatus.NetworkError || updateApplicationRequest.type === OpenApiRequestStatus.ServerError ||
            deleteApplicationRequest.type === OpenApiRequestStatus.NetworkError || deleteApplicationRequest.type === OpenApiRequestStatus.ServerError) {
            setApplicationRequestPending(false);
            setModalUpdateError(true);
        }
        // eslint-disable-next-line
    }, [updateApplicationRequest, deleteApplicationRequest]);

    function handleClick(clickedModal: string) {
        if (clickedModal === 'Accept' || clickedModal === 'Decline') {
            updateApplication({ ...props.application, status: clickedModal === 'Accept' ? ApplicationStatus.ACCEPTED : ApplicationStatus.DECLINED });
        } else if (clickedModal === 'Delete') {
            deleteApplication(props.application.id);
        }
    }

    const handleClose = () => {
        setShowModal(false);
        setModalUpdateError(false);
    }

    return (
        <TableRow>
            <TableColumn style={{ 'minWidth': '120px' }}>{props.application.requesteeCustomerNumber}</TableColumn>
            <TableColumn>{props.type === ViewType.SENT_APPLICATIONS ? props.application.requesteeUsername : props.application.requestorUser?.username}</TableColumn>
            {props.type === ViewType.RECEIVED_APPLICATIONS && <TableColumn> {props.application.requestorUser?.firstName + " " + props.application.requestorUser?.lastName}</TableColumn>}
            <TableColumn>{startDate} - <b>{endDate}</b></TableColumn>
            {props.type === ViewType.SENT_APPLICATIONS &&
                <TableColumn>{createdDate}</TableColumn>}
            <TableColumn>
                <Status theme={{ status: (props.application.status).replace('_BY_ADMIN', '') }}> {t(applicationStatusToString(props.application.status))}  </Status>
            </TableColumn>

            {props.application.status === ApplicationStatus.PENDING && props.type === ViewType.RECEIVED_APPLICATIONS &&
                <TableColumn>
                    <div style={{display: 'flex'}}>
                        <ActionButton
                            aria-labelledby="declineButton" 
                            id="declineButton" 
                            theme={{ actionType: 'Decline' }} 
                            onClick={() => { setShowModal(true); setModalType('Decline'); }}>
                            <DeclineIcon />
                        </ActionButton>
                        <ActionButton 
                            aria-labelledby="acceptButton" 
                            id="acceptButton" 
                            theme={{ actionType: 'Accept' }} 
                            onClick={() => { setShowModal(true); setModalType('Accept'); }}>
                            <AcceptIcon />
                        </ActionButton>
                    </div>
                    <ApplicationModal
                        show={showModal}
                        modalType={modalType}
                        application={props.application}
                        closeModal={handleClose}
                        handleClick={handleClick}
                        showError={modalUpdateError}
                        applicationRequestPending={applicationRequestPending} />
                </TableColumn>
            }

            {props.type === ViewType.SENT_APPLICATIONS &&
                <TableColumn style={{ 'width': '50px' }}>
                    {props.application.status === ApplicationStatus.PENDING &&
                        <ActionButton 
                            aria-labelledby="declineButton" 
                            id="declineButton" 
                            theme={{ actionType: 'Decline' }} 
                            onClick={() => { setShowModal(true); setModalType('Delete'); }}>
                            <DeclineIcon />
                        </ActionButton>}
                    <ApplicationModal
                        show={showModal}
                        modalType={modalType}
                        application={props.application}
                        closeModal={handleClose}
                        handleClick={handleClick}
                        showError={modalUpdateError}
                        applicationRequestPending={applicationRequestPending} />
                </TableColumn>}
        </TableRow>
    )
}

export default ApplicationItem;